/**
 * GlobalSelectionToolbar — Platform-wide text selection toolbar.
 *
 * Sole popup for text selections anywhere in the app. Mounted ONCE at
 * the root (see `__root.tsx`). The previous local
 * `ask-argus/selection-toolbar.tsx` was a duplicate that fired its own
 * listeners on the chat container and produced the "double popup" bug
 * (PR-AAU-D2). That component is gone.
 *
 * Actions (PR-AAU-D2 locked behaviour):
 *
 *   - Ask     — pushes the highlighted text into the global attached-
 *               highlights queue, which the Ask Argus composer reads
 *               and renders as a chip above the textarea. No mini
 *               overlay. Sending the chat turn prepends the snippet to
 *               the message body as a markdown blockquote. Works on
 *               any page; when the user lands on Ask Argus the chips
 *               appear.
 *
 *   - Explain — opens an inline mini-overlay anchored to the selection.
 *               Immediately fires a request to `/api/v1/ecosystem/query`
 *               (same endpoint as the full Ask Argus chat) asking the
 *               LLM to explain the snippet in plain language. Streams
 *               the response tokens into the overlay body. A follow-up
 *               input at the bottom of the overlay lets the user keep
 *               asking about the same snippet without leaving the page
 *               they're on.
 *
 *   - Copy    — `navigator.clipboard.writeText(selectedText)`. Closes
 *               the toolbar on success.
 *
 * The toolbar auto-dismisses on:
 *   - click outside
 *   - selection cleared (mouseup with empty range, only when in the
 *     buttons state — once the user has opened an inline overlay the
 *     selection clearing should NOT close the overlay)
 *   - Escape key
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageCircleQuestion,
  Lightbulb,
  Copy,
  Check,
  Send,
  X,
  Loader2,
} from 'lucide-react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { ArgusEye } from '@/components/icons';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { useAttachedHighlights } from '@/contexts/attached-highlights-context';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type ToolbarState = 'buttons' | 'response';

interface OverlayMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function GlobalSelectionToolbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { add: addHighlight } = useAttachedHighlights();

  const toolbarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [state, setState] = useState<ToolbarState>('buttons');
  const [copied, setCopied] = useState(false);

  // Explain overlay state
  const [chatHistory, setChatHistory] = useState<OverlayMessage[]>([]);
  const [responding, setResponding] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [streamError, setStreamError] = useState<string | null>(null);

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setVisible(false);
    setState('buttons');
    setCopied(false);
    setChatHistory([]);
    setResponding(false);
    setFollowUp('');
    setStreamError(null);
  }, []);

  // Detect text selection anywhere in the document
  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Clicks inside the toolbar are toolbar actions, not new selections.
      if (toolbarRef.current?.contains(e.target as Node)) return;

      // Browsers finalise selection just after the mouseup event; wait
      // a tick before reading it.
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.toString().trim()) {
          // Only close in the buttons state. Once an inline overlay is
          // open, clearing the original selection (e.g. by clicking the
          // overlay's input) must NOT dismiss the overlay.
          if (state === 'buttons' && !toolbarRef.current?.contains(document.activeElement)) {
            close();
          }
          return;
        }

        // Skip selections inside form controls — they have their own UX.
        const anchorNode = selection.anchorNode;
        if (anchorNode) {
          const el = anchorNode instanceof Element ? anchorNode : anchorNode.parentElement;
          if (el?.closest('input, textarea, [contenteditable="true"], [role="textbox"]')) {
            return;
          }
        }

        const text = selection.toString().trim();
        if (text.length < 3) return; // ignore stray double-clicks

        setSelectedText(text);

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setPos({
          x: rect.left + rect.width / 2,
          y: rect.bottom + window.scrollY + 8,
        });
        setVisible(true);
        setState('buttons');
      }, 10);
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Prevent selection collapse when clicking toolbar buttons.
      if (toolbarRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [close, state]);

  // ── Actions ──────────────────────────────────────────────────────────

  const handleCopy = () => {
    navigator.clipboard.writeText(selectedText).then(
      () => {
        setCopied(true);
        setTimeout(() => close(), 800);
      },
      () => {
        toast.error('Could not copy to clipboard');
      },
    );
  };

  const handleAsk = () => {
    // Push into the global queue. The Ask Argus composer reads this and
    // renders the snippet as a chip above the textarea.
    addHighlight(selectedText, location.pathname);

    const onAskArgusPage = location.pathname.startsWith('/ask-argus');
    if (onAskArgusPage) {
      toast.success('Highlight attached to chat');
    } else {
      // User is somewhere else (e.g. /library, /admin) — route them to
      // Ask Argus so they see the chip and can ask their question.
      toast.success('Highlight attached — open Ask Argus to send');
      void navigate({ to: '/ask-argus' });
    }
    close();
  };

  const handleExplain = () => {
    setChatHistory([
      {
        role: 'user',
        content: `Explain the following highlighted text in plain language:\n\n"${selectedText}"`,
      },
    ]);
    setState('response');
    void streamExplain(`Explain the following highlighted text from the page (${location.pathname}) in plain language. Keep it concise (3-5 sentences) unless the user follows up.\n\nHighlighted text:\n"""\n${selectedText}\n"""`);
  };

  const handleFollowUp = () => {
    const text = followUp.trim();
    if (!text || responding) return;
    setFollowUp('');
    setChatHistory((prev) => [...prev, { role: 'user', content: text }]);
    // Use the entire conversation as context — the snippet was in the
    // first user turn, so the LLM already has it.
    void streamExplain(text);
  };

  /**
   * Stream a turn through `/api/v1/ecosystem/query`. We append an empty
   * assistant message and grow its content as `token` SSE events land.
   *
   * The endpoint is conversationless (conversationId: null) on purpose —
   * the inline overlay is a transient side-conversation, not something
   * we want to persist into the user's main chat history.
   */
  const streamExplain = useCallback(
    async (prompt: string) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setResponding(true);
      setStreamError(null);
      setChatHistory((prev) => [...prev, { role: 'assistant', content: '' }]);

      const accessToken = (() => {
        try {
          return localStorage.getItem('accessToken');
        } catch {
          return null;
        }
      })();

      let res: Response;
      try {
        res = await fetch('/api/v1/ecosystem/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            query: prompt,
            conversationId: null,
            options: { ragEnabled: true },
          }),
          signal: ac.signal,
        });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setStreamError('Network error');
        setResponding(false);
        return;
      }

      if (!res.ok || !res.body) {
        setStreamError(`Ask Argus error (${res.status})`);
        setResponding(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const appendToken = (chunk: string) => {
        setChatHistory((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = { ...last, content: last.content + chunk };
          }
          return next;
        });
      };

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let sepIdx: number;
          while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);
            const dataLines = raw
              .split('\n')
              .filter((l) => l.startsWith('data:'))
              .map((l) => l.slice(5).trim());
            if (dataLines.length === 0) continue;
            const payload = dataLines.join('\n');
            let evt: { type: string; data: unknown };
            try {
              evt = JSON.parse(payload);
            } catch {
              continue;
            }
            switch (evt.type) {
              case 'token': {
                const d = evt.data as { content?: string };
                if (typeof d.content === 'string') appendToken(d.content);
                break;
              }
              case 'error': {
                const d = evt.data as { code?: string; message?: string };
                setStreamError(d.message ?? d.code ?? 'Stream error');
                break;
              }
              default:
                break;
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setStreamError('Stream interrupted');
        }
      } finally {
        setResponding(false);
      }
    },
    [],
  );

  // Focus the follow-up input the moment the response finishes streaming.
  useEffect(() => {
    if (state === 'response' && !responding && chatHistory.some((m) => m.role === 'assistant' && m.content)) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [state, responding, chatHistory]);

  if (!visible) return null;

  return (
    <div
      ref={toolbarRef}
      className="fixed z-[100] animate-in fade-in slide-in-from-bottom-1 duration-150"
      style={{ left: `${pos.x}px`, top: `${pos.y}px`, transform: 'translateX(-50%)' }}
      data-testid="global-selection-toolbar"
    >
      {state === 'buttons' && (
        <div className="flex items-center gap-0.5 bg-popover border border-primary/30 rounded-lg shadow-xl ring-1 ring-primary/10 px-1 py-0.5">
          <button
            onClick={handleAsk}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary hover:bg-primary/10 transition-colors"
            title="Attach this text to the Ask Argus chat composer"
          >
            <MessageCircleQuestion className="h-3.5 w-3.5" /> Ask
          </button>
          <button
            onClick={handleExplain}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary hover:bg-primary/10 transition-colors"
            title="Explain this text inline"
          >
            <Lightbulb className="h-3.5 w-3.5" /> Explain
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary hover:bg-primary/10 transition-colors"
            title="Copy to clipboard"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}

      {state === 'response' && (
        <div className="bg-popover border border-border rounded-lg shadow-lg w-96 max-h-80 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5 shrink-0 border-b border-border/30">
            <div className="flex items-center gap-1.5">
              <ArgusEye
                state={responding ? 'thinking' : streamError ? 'alert' : 'idle'}
                blink={responding ? 'fast' : 'none'}
                className="h-4 w-4 text-primary"
              />
              <span className="text-[10px] font-medium">Argus AI</span>
            </div>
            <button
              onClick={close}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
              title="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto px-3 py-2">
            {chatHistory.map((msg, i) => (
              <div key={i} className={cn('mb-2', msg.role === 'user' ? 'text-right' : '')}>
                {msg.role === 'user' ? (
                  <span className="inline-block bg-primary text-primary-foreground rounded-lg rounded-tr-sm px-2.5 py-1.5 text-xs max-w-[85%] text-left whitespace-pre-wrap">
                    {msg.content}
                  </span>
                ) : (
                  <div className="text-xs">
                    {msg.content ? (
                      <MarkdownRenderer content={msg.content} />
                    ) : responding ? (
                      <div className="flex items-center gap-1.5 text-muted-foreground py-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> Thinking...
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
            {streamError && (
              <div className="text-[11px] text-destructive border border-destructive/30 bg-destructive/5 rounded px-2 py-1.5 mt-1">
                {streamError}
              </div>
            )}
          </div>
          <div className="px-2.5 pb-2.5 pt-1.5 border-t border-border/30 shrink-0">
            <div className="flex gap-1.5">
              <input
                ref={inputRef}
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && followUp.trim() && !responding) handleFollowUp();
                  if (e.key === 'Escape') close();
                }}
                disabled={responding}
                placeholder={responding ? 'Argus is replying…' : 'Ask a follow-up…'}
                className="flex-1 h-7 px-2 text-xs rounded-md border border-input bg-transparent disabled:opacity-50"
                aria-label="Follow-up question"
              />
              <button
                onClick={handleFollowUp}
                disabled={!followUp.trim() || responding}
                className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 shrink-0"
                aria-label="Submit follow-up"
                title="Submit"
              >
                <Send className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <button
                onClick={() => {
                  const aiText = chatHistory
                    .filter((m) => m.role === 'assistant')
                    .map((m) => m.content)
                    .join('\n\n');
                  if (aiText) {
                    navigator.clipboard.writeText(aiText);
                    toast.success('Copied');
                  }
                }}
                className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Copy className="h-2.5 w-2.5" /> Copy reply
              </button>
              <button
                onClick={close}
                className="text-[10px] text-muted-foreground hover:text-foreground ml-auto"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
